/**
 * Signing Approvals — admin surface for the code-signing gate.
 *
 * This is the signing-request APPROVAL workflow (/signing/*) — the
 * manual-review queue, automated-tier oversight, and the immutable audit
 * log of every signed binary. Distinct from the "Code Signing & AV"
 * status surface (/admin-services/code-signing).
 *
 * Three tabs (mirroring the legacy dashboard view):
 *   1. Overview      — stats + KPIs
 *   2. Pending Queue — requests awaiting admin review (default), with a
 *                      click-through request-detail panel showing the full
 *                      validation trail + a per-creator tier control
 *   3. Audit Log     — every signed binary, filterable by SHA-256 / name,
 *                      with revoke
 *
 * Envelope: every /signing/* endpoint is `{ success, data }` enveloped
 * (the legacy hook peeled it with `.then(unwrap)`); the typed client in
 * @/api/signing peels it with the admin `unwrap()` helper. Mutations
 * invalidate the ['signing'] query family on success, matching legacy.
 */
import { useMemo, useState } from 'react';
import { Shield, Check, AlertCircle, Clock, ExternalLink } from 'lucide-react';
import {
  AuditLogEntry,
  AuditLogListParams,
  CreatorRef,
  SigningRequest,
  SigningRequestListParams,
  SigningRequestStatus,
  SigningTier,
  ValidationCheck
} from '@/api/signing';
import {
  useApproveSigningRequest,
  useAuditLog,
  useRejectSigningRequest,
  useRevokeSignedBinary,
  useSetCreatorTier,
  useSigningRequest,
  useSigningRequests,
  useSigningStats
} from '@/hooks/useSigning';
import {
  ActionButton,
  BackButton,
  Card,
  Pagination,
  SectionLabel,
  Select,
  Skeleton,
  StatCard
} from '@/components/ui/atoms';
import { fmtDateTime, fmtNum } from '@/lib/format';

// ─── Status pill ──────────────────────────────────────────────────────────
// The shared Badge atom's map doesn't cover the signing-specific lifecycle
// (building / auto_rejected / signed / shipped / revoked / cancelled /
// approved), so we keep a local pill mirroring the legacy STATUS_COLORS
// semantics, expressed against the amber-accent token palette.

const STATUS_PILL: Record<string, string> = {
  pending: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  approved: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  rejected: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30',
  auto_rejected: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30',
  building: 'bg-purple-500/20 text-purple-300 ring-1 ring-purple-500/30',
  completed: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  failed: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30',
  cancelled: 'bg-white/10 text-nurev-muted ring-1 ring-white/10',
  signed: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  shipped: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  revoked: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30'
};

function StatusPill({ status }: { status?: string | null }) {
  const cls =
    STATUS_PILL[status || ''] || 'bg-white/10 text-nurev-muted ring-1 ring-white/10';
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}
    >
      {status || '—'}
    </span>
  );
}

const TIER_OPTIONS: Array<{ value: SigningTier; label: string }> = [
  { value: 'none', label: 'none' },
  { value: 'manual-review', label: 'manual-review' },
  { value: 'automated', label: 'automated' }
];

const shortId = (id?: string | null): string =>
  id ? String(id).slice(-8) : '—';

/** Resolve a (possibly populated) creator ref's email, falling back to a short id. */
function creatorEmail(ref?: CreatorRef | string | null): string {
  if (!ref) return '—';
  if (typeof ref === 'string') return shortId(ref);
  return ref.email || shortId(ref._id);
}

function creatorObj(ref?: CreatorRef | string | null): CreatorRef | null {
  return ref && typeof ref === 'object' ? ref : null;
}

// ─── Overview tab ───────────────────────────────────────────────────────────

function OverviewTab() {
  const { data: stats, isLoading } = useSigningStats();

  const byStatus = useMemo(
    () =>
      (stats?.requestsByStatus || []).reduce<Record<string, number>>(
        (acc, r) => {
          acc[r._id || 'none'] = r.count;
          return acc;
        },
        {}
      ),
    [stats]
  );
  const byTier = useMemo(
    () =>
      (stats?.creatorsByTier || []).reduce<Record<string, number>>((acc, r) => {
        acc[r._id || 'none'] = r.count;
        return acc;
      }, {}),
    [stats]
  );

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      <StatCard
        label="Pending"
        value={fmtNum(byStatus.pending || 0)}
        sub="Awaiting admin review"
        color="text-amber-300"
        loading={isLoading}
      />
      <StatCard
        label="Signed (last 30 days)"
        value={fmtNum(stats?.signedBinaries?.last30d || 0)}
        sub={`${fmtNum(stats?.signedBinaries?.total || 0)} total ever`}
        color="text-emerald-300"
        loading={isLoading}
      />
      <StatCard
        label="Revoked"
        value={fmtNum(stats?.signedBinaries?.revoked || 0)}
        sub="Binaries pulled from distribution"
        color="text-red-300"
        loading={isLoading}
      />
      <StatCard
        label="Automated-tier creators"
        value={fmtNum(byTier.automated || 0)}
        sub={`${fmtNum(byTier['manual-review'] || 0)} on manual review`}
        color="text-purple-300"
        loading={isLoading}
      />
    </div>
  );
}

// ─── Pending Queue tab ──────────────────────────────────────────────────────

function PendingQueueTab() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<SigningRequestStatus | 'all'>('pending');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<SigningRequest | null>(null);
  const [banner, setBanner] = useState<{
    kind: 'ok' | 'err';
    text: string;
  } | null>(null);

  const params = useMemo<SigningRequestListParams>(
    () => ({
      page,
      limit: 25,
      status: status === 'all' ? '' : status
    }),
    [page, status]
  );
  // Match the legacy view's 15s queue refresh.
  const { data, isLoading } = useSigningRequests(params, {
    refetchInterval: 15_000
  });
  const approve = useApproveSigningRequest();
  const reject = useRejectSigningRequest();

  const docs = data?.docs || [];
  const totalPages = data?.totalPages || 1;

  const handleApprove = async (id: string) => {
    setBanner(null);
    try {
      await approve.mutateAsync(id);
      setBanner({ kind: 'ok', text: 'Request approved — build enqueued' });
    } catch (err) {
      setBanner({ kind: 'err', text: extractErr(err, 'Approve failed') });
    }
  };

  const submitReject = async (reason: string) => {
    if (!rejectTarget) return;
    setBanner(null);
    try {
      await reject.mutateAsync({ id: rejectTarget._id, reason });
      setBanner({ kind: 'ok', text: 'Request rejected' });
      setRejectTarget(null);
    } catch (err) {
      setBanner({ kind: 'err', text: extractErr(err, 'Reject failed') });
    }
  };

  if (detailId) {
    return (
      <RequestDetailPanel id={detailId} onBack={() => setDetailId(null)} />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select<SigningRequestStatus | 'all'>
          value={status}
          onChange={v => {
            setStatus(v);
            setPage(1);
          }}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'pending', label: 'pending' },
            { value: 'approved', label: 'approved' },
            { value: 'rejected', label: 'rejected' },
            { value: 'auto_rejected', label: 'auto_rejected' },
            { value: 'building', label: 'building' },
            { value: 'completed', label: 'completed' },
            { value: 'failed', label: 'failed' },
            { value: 'cancelled', label: 'cancelled' }
          ]}
        />
      </div>

      {banner && (
        <p
          className={`text-xs ${
            banner.kind === 'ok' ? 'text-emerald-300' : 'text-red-300'
          }`}
        >
          {banner.text}
        </p>
      )}

      {isLoading ? (
        <Card>
          <Skeleton className="h-40 w-full" />
        </Card>
      ) : docs.length === 0 ? (
        <Card className="p-8 text-center">
          <Clock className="mx-auto mb-2 h-6 w-6 text-amber-300" />
          <p className="mb-1 text-sm font-semibold text-nurev-text">
            Queue is empty
          </p>
          <p className="text-xs text-nurev-muted">
            No signing requests match this filter. Auto-graduated creators
            ship directly to the audit log.
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-nurev-border text-left text-[10px] uppercase tracking-wider text-nurev-muted">
                  <th className="px-4 py-3 font-medium">Product</th>
                  <th className="px-4 py-3 font-medium">Creator</th>
                  <th className="px-4 py-3 font-medium">Platform</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Requested</th>
                  <th className="px-4 py-3 font-medium">Checks</th>
                  <th className="px-4 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {docs.map(req => (
                  <QueueRow
                    key={req._id}
                    req={req}
                    approving={approve.isPending}
                    rejecting={reject.isPending}
                    onApprove={() => handleApprove(req._id)}
                    onReject={() => setRejectTarget(req)}
                    onOpen={() => setDetailId(req._id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="border-t border-nurev-border px-4 py-3">
              <Pagination
                page={page}
                totalPages={totalPages}
                onChange={setPage}
              />
            </div>
          )}
        </Card>
      )}

      {rejectTarget && (
        <RejectModal
          target={rejectTarget}
          submitting={reject.isPending}
          onCancel={() => setRejectTarget(null)}
          onSubmit={submitReject}
        />
      )}
    </div>
  );
}

function QueueRow({
  req,
  approving,
  rejecting,
  onApprove,
  onReject,
  onOpen
}: {
  req: SigningRequest;
  approving: boolean;
  rejecting: boolean;
  onApprove: () => void;
  onReject: () => void;
  onOpen: () => void;
}) {
  const creator = creatorObj(req.creatorId);
  const failedChecks = (req.validationChecks || []).filter(c => !c.pass);
  const isPending = req.status === 'pending';

  return (
    <tr className="border-b border-nurev-border/50 hover:bg-white/[0.02]">
      <td className="px-4 py-3">
        <button
          onClick={onOpen}
          className="text-left text-sm font-semibold text-nurev-text hover:text-amber-300"
        >
          {req.buildParams?.productName || '—'}
        </button>
        <p className="font-mono text-[11px] text-nurev-muted">
          {req.buildParams?.companyName || ''}
        </p>
      </td>
      <td className="px-4 py-3">
        <p className="text-xs text-nurev-text">{creatorEmail(req.creatorId)}</p>
        {creator?.publisherKey && (
          <p className="font-mono text-[10px] text-nurev-muted">
            {creator.publisherKey}
          </p>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-nurev-text">
        {req.buildParams?.platform || '—'}
      </td>
      <td className="px-4 py-3">
        <StatusPill status={req.status} />
      </td>
      <td className="px-4 py-3 text-[11px] text-nurev-muted">
        {fmtDateTime(req.createdAt)}
      </td>
      <td className="px-4 py-3">
        {failedChecks.length === 0 ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300">
            <Check className="h-3 w-3" strokeWidth={3} />
            All passed
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1 text-[11px] text-amber-300"
            title={failedChecks.map(c => `${c.name}: ${c.note || ''}`).join('\n')}
          >
            <AlertCircle className="h-3 w-3" />
            {failedChecks.length} failed
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {isPending ? (
          <div className="inline-flex items-center gap-2">
            <ActionButton
              size="sm"
              variant="positive"
              disabled={approving}
              onClick={onApprove}
            >
              Approve
            </ActionButton>
            <ActionButton
              size="sm"
              variant="neutral"
              disabled={rejecting}
              onClick={onReject}
            >
              Reject
            </ActionButton>
          </div>
        ) : (
          <button
            onClick={onOpen}
            className="text-[11px] text-amber-300 hover:text-amber-200"
          >
            detail
          </button>
        )}
      </td>
    </tr>
  );
}

function RejectModal({
  target,
  submitting,
  onCancel,
  onSubmit
}: {
  target: SigningRequest;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-xl border border-nurev-border bg-nurev-surface-lift p-5"
        onClick={e => e.stopPropagation()}
      >
        <p className="mb-1 text-sm font-semibold text-nurev-text">
          Reject signing request
        </p>
        <p className="mb-4 text-xs text-nurev-muted">
          {target.buildParams?.productName || '—'} for{' '}
          {creatorEmail(target.creatorId)}
        </p>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Reason (shared with the creator)"
          rows={4}
          className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text placeholder:text-nurev-muted focus:border-amber-500/50 focus:outline-none"
        />
        <div className="mt-4 flex items-center justify-end gap-2">
          <ActionButton variant="neutral" size="sm" onClick={onCancel}>
            Cancel
          </ActionButton>
          <ActionButton
            variant="destructive"
            size="sm"
            disabled={submitting}
            onClick={() => onSubmit(reason || 'Rejected by admin')}
          >
            Reject
          </ActionButton>
        </div>
      </div>
    </div>
  );
}

// ─── Request detail panel (with per-creator tier control) ────────────────────

function RequestDetailPanel({
  id,
  onBack
}: {
  id: string;
  onBack: () => void;
}) {
  const { data: req, isLoading } = useSigningRequest(id);

  return (
    <div className="space-y-4">
      <BackButton onClick={onBack} label="Back to queue" />

      {isLoading ? (
        <Card>
          <Skeleton className="h-40 w-full" />
        </Card>
      ) : !req ? (
        <Card>
          <p className="text-sm text-nurev-muted">Signing request not found.</p>
        </Card>
      ) : (
        <>
          <Card>
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <p className="text-lg font-bold text-nurev-text">
                  {req.buildParams?.productName || '—'}
                </p>
                <p className="font-mono text-xs text-nurev-muted">
                  {req.buildParams?.companyName || ''}
                </p>
              </div>
              <StatusPill status={req.status} />
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
              <Detail label="Platform">
                {req.buildParams?.platform || '—'}
              </Detail>
              <Detail label="Creator">{creatorEmail(req.creatorId)}</Detail>
              <Detail label="Approval Tier">{req.approvalTier || '—'}</Detail>
              <Detail label="Requested">{fmtDateTime(req.createdAt)}</Detail>
            </div>

            {req.rejectionReason && (
              <div className="mt-4 rounded-lg bg-red-500/10 p-3">
                <p className="text-[10px] uppercase text-nurev-muted">
                  Rejection reason
                </p>
                <p className="text-sm text-red-300">{req.rejectionReason}</p>
              </div>
            )}
          </Card>

          <Card>
            <SectionLabel>Validation Checks</SectionLabel>
            <ValidationChecks checks={req.validationChecks} />
          </Card>

          <CreatorTierCard creator={creatorObj(req.creatorId)} />
        </>
      )}
    </div>
  );
}

function ValidationChecks({ checks }: { checks?: ValidationCheck[] }) {
  if (!checks || checks.length === 0) {
    return <p className="text-sm text-nurev-muted">No validation checks recorded.</p>;
  }
  return (
    <div className="space-y-2">
      {checks.map((c, i) => (
        <div
          key={`${c.name}-${i}`}
          className="flex items-start justify-between gap-3 rounded-lg bg-white/[0.03] p-3"
        >
          <div>
            <p className="text-sm text-nurev-text">{c.name}</p>
            {c.note && <p className="text-xs text-nurev-muted">{c.note}</p>}
          </div>
          {c.pass ? (
            <span className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] text-emerald-300">
              <Check className="h-3 w-3" strokeWidth={3} />
              pass
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] text-amber-300">
              <AlertCircle className="h-3 w-3" />
              fail
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function CreatorTierCard({ creator }: { creator: CreatorRef | null }) {
  const setTier = useSetCreatorTier();
  const [tier, setTierValue] = useState<SigningTier>(
    creator?.signingTier || 'none'
  );
  const [banner, setBanner] = useState<{
    kind: 'ok' | 'err';
    text: string;
  } | null>(null);

  if (!creator) {
    return (
      <Card>
        <SectionLabel>Creator Signing Tier</SectionLabel>
        <p className="text-sm text-nurev-muted">
          Creator details not available on this request.
        </p>
      </Card>
    );
  }

  const handleSave = async () => {
    setBanner(null);
    try {
      await setTier.mutateAsync({ creatorId: creator._id, tier });
      setBanner({ kind: 'ok', text: `Tier set to ${tier}` });
    } catch (err) {
      setBanner({ kind: 'err', text: extractErr(err, 'Failed to set tier') });
    }
  };

  return (
    <Card>
      <SectionLabel>Creator Signing Tier</SectionLabel>
      <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
        <Detail label="Creator">{creator.email || shortId(creator._id)}</Detail>
        <Detail label="Publisher Key">{creator.publisherKey || '—'}</Detail>
        <Detail label="ID Verification">
          {creator.identityVerificationStatus || '—'}
        </Detail>
      </div>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div>
          <p className="mb-1 text-[10px] uppercase text-nurev-muted">Tier</p>
          <Select<SigningTier>
            value={tier}
            onChange={setTierValue}
            options={TIER_OPTIONS}
          />
        </div>
        <ActionButton
          size="sm"
          disabled={setTier.isPending || tier === (creator.signingTier || 'none')}
          onClick={handleSave}
        >
          {setTier.isPending ? 'Saving…' : 'Save tier'}
        </ActionButton>
        {banner && (
          <span
            className={`text-xs ${
              banner.kind === 'ok' ? 'text-emerald-300' : 'text-red-300'
            }`}
          >
            {banner.text}
          </span>
        )}
      </div>
    </Card>
  );
}

function Detail({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-[10px] uppercase text-nurev-muted">{label}</p>
      <p className="text-sm text-nurev-text">{children}</p>
    </div>
  );
}

// ─── Audit Log tab ──────────────────────────────────────────────────────────

function AuditLogTab() {
  const [page, setPage] = useState(1);
  const [sha256, setSha256] = useState('');
  const [query, setQuery] = useState('');
  const [revokeTarget, setRevokeTarget] = useState<AuditLogEntry | null>(null);
  const [banner, setBanner] = useState<{
    kind: 'ok' | 'err';
    text: string;
  } | null>(null);

  const params = useMemo<AuditLogListParams>(
    () => ({
      page,
      limit: 25,
      sha256: sha256 || undefined,
      q: query || undefined
    }),
    [page, sha256, query]
  );
  const { data, isLoading } = useAuditLog(params);
  const revoke = useRevokeSignedBinary();

  const docs = data?.docs || [];
  const totalPages = data?.totalPages || 1;

  const submitRevoke = async (reason: string) => {
    if (!revokeTarget) return;
    setBanner(null);
    try {
      await revoke.mutateAsync({ id: revokeTarget._id, reason });
      setBanner({ kind: 'ok', text: 'Binary marked as revoked' });
      setRevokeTarget(null);
    } catch (err) {
      setBanner({ kind: 'err', text: extractErr(err, 'Revoke failed') });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={sha256}
          onChange={e => {
            setSha256(e.target.value.trim());
            setPage(1);
          }}
          placeholder="Lookup by SHA-256 hash"
          className="min-w-[220px] flex-1 rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 font-mono text-xs text-nurev-text placeholder:text-nurev-muted focus:border-amber-500/50 focus:outline-none"
        />
        <input
          type="text"
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setPage(1);
          }}
          placeholder="Product name…"
          className="w-56 rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-xs text-nurev-text placeholder:text-nurev-muted focus:border-amber-500/50 focus:outline-none"
        />
      </div>

      {banner && (
        <p
          className={`text-xs ${
            banner.kind === 'ok' ? 'text-emerald-300' : 'text-red-300'
          }`}
        >
          {banner.text}
        </p>
      )}

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-nurev-border text-left text-[10px] uppercase tracking-wider text-nurev-muted">
                <th className="px-4 py-3 font-medium">Product</th>
                <th className="px-4 py-3 font-medium">Creator</th>
                <th className="px-4 py-3 font-medium">Platform</th>
                <th className="px-4 py-3 font-medium">Tier</th>
                <th className="px-4 py-3 font-medium">Signed</th>
                <th className="px-4 py-3 font-medium">SHA-256</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-nurev-border/50">
                    {Array.from({ length: 8 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : docs.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-12 text-center text-nurev-muted"
                  >
                    No audit entries match.
                  </td>
                </tr>
              ) : (
                docs.map(row => (
                  <AuditRow
                    key={row._id}
                    row={row}
                    onRevoke={() => setRevokeTarget(row)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="border-t border-nurev-border px-4 py-3">
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </Card>

      {revokeTarget && (
        <RevokeModal
          target={revokeTarget}
          submitting={revoke.isPending}
          onCancel={() => setRevokeTarget(null)}
          onSubmit={submitRevoke}
        />
      )}
    </div>
  );
}

function AuditRow({
  row,
  onRevoke
}: {
  row: AuditLogEntry;
  onRevoke: () => void;
}) {
  return (
    <tr className="border-b border-nurev-border/50 hover:bg-white/[0.02]">
      <td className="px-4 py-3 text-xs text-nurev-text">
        {row.buildParams?.productName || '—'}
      </td>
      <td className="px-4 py-3 text-xs text-nurev-muted">
        {row.creatorEmailAtSigning || creatorEmail(row.creatorId)}
      </td>
      <td className="px-4 py-3 text-[11px] text-nurev-muted">
        {row.buildParams?.platform || '—'}
      </td>
      <td className="px-4 py-3 text-[11px] text-nurev-muted">
        {row.approvalTier || '—'}
      </td>
      <td className="px-4 py-3 text-[11px] text-nurev-muted">
        {fmtDateTime(row.createdAt)}
      </td>
      <td className="px-4 py-3 font-mono text-[11px] text-nurev-muted">
        {row.binarySha256 ? `${row.binarySha256.slice(0, 12)}…` : '—'}
      </td>
      <td className="px-4 py-3">
        <StatusPill status={row.status} />
      </td>
      <td className="px-4 py-3 text-right">
        {row.status !== 'revoked' && (
          <ActionButton size="sm" variant="neutral" onClick={onRevoke}>
            Revoke
          </ActionButton>
        )}
      </td>
    </tr>
  );
}

function RevokeModal({
  target,
  submitting,
  onCancel,
  onSubmit
}: {
  target: AuditLogEntry;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-xl border border-nurev-border bg-nurev-surface-lift p-5"
        onClick={e => e.stopPropagation()}
      >
        <p className="mb-1 text-sm font-semibold text-nurev-text">
          Revoke signed binary
        </p>
        <p className="mb-4 text-xs text-nurev-muted">
          {target.buildParams?.productName || '—'}
          {target.binarySha256 ? ` · ${target.binarySha256.slice(0, 12)}…` : ''}
        </p>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Revocation reason (stored in the audit log)"
          rows={4}
          className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text placeholder:text-nurev-muted focus:border-amber-500/50 focus:outline-none"
        />
        <div className="mt-4 flex items-center justify-end gap-2">
          <ActionButton variant="neutral" size="sm" onClick={onCancel}>
            Cancel
          </ActionButton>
          <ActionButton
            variant="destructive"
            size="sm"
            disabled={submitting || !reason.trim()}
            onClick={() => onSubmit(reason)}
          >
            Revoke
          </ActionButton>
        </div>
      </div>
    </div>
  );
}

// ─── Error extraction ───────────────────────────────────────────────────────

function extractErr(err: unknown, fallback: string): string {
  const e = err as {
    response?: { data?: { error?: string; message?: string } };
  };
  return e?.response?.data?.error || e?.response?.data?.message || fallback;
}

// ─── Main screen ──────────────────────────────────────────────────────────

const TABS = [
  { id: 'queue', label: 'Pending Queue' },
  { id: 'overview', label: 'Overview' },
  { id: 'audit', label: 'Audit Log' }
] as const;
type Tab = (typeof TABS)[number]['id'];

export function SigningApprovalsScreen() {
  const [tab, setTab] = useState<Tab>('queue');

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Shield className="mt-1 h-5 w-5 text-amber-300" />
          <div>
            <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
              Signing Approvals
            </h1>
            <p className="mt-1 text-xs text-nurev-muted">
              Manual-review queue, automated-tier oversight, and the immutable
              audit log of every signed binary.
            </p>
          </div>
        </div>
        <a
          href="https://app.nurev.io/signing-approvals"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-nurev-muted hover:text-amber-300"
          title="Open the legacy view on the publisher dashboard"
        >
          legacy view
          <ExternalLink className="h-3 w-3" />
        </a>
      </header>

      <div className="flex w-fit gap-1 rounded-lg bg-white/[0.03] p-1">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`rounded-md px-4 py-2 text-xs font-medium transition ${
              tab === t.id
                ? 'bg-amber-500 text-nurev-bg'
                : 'text-nurev-muted hover:bg-white/[0.04] hover:text-nurev-text'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'queue' && <PendingQueueTab />}
      {tab === 'overview' && <OverviewTab />}
      {tab === 'audit' && <AuditLogTab />}
    </div>
  );
}
